/**
 * <HeaderCreateButton /> — Feb 2026 · Global "+ Create" dropdown in the
 * app header. Provides one-click shortcuts to the most-created objects
 * so users don't need to navigate to each module first.
 *
 *   • Create Task      → opens QuickAddTaskPopover in-place
 *                        (no navigation to Tasks module) with triple
 *                        post-save action: Save & Close / Save & Go to
 *                        Tasks / Save & Open Task.  — R293zze
 *   • Create Project   → /app/projects?new=1     (auto-opens NewProjectDialog)
 *   • Create Milestone → /app/projects           (toast asks user to pick a project first,
 *                                                 since milestones are project-scoped)
 *   • Create Package   → /app/packages?new=1     (auto-opens NewPackageDialog)
 *
 * Closes on outside click / Escape. Keyboard-navigable via native
 * button focus order.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CheckSquare, FolderKanban, Flag, Package as PackageIcon, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { QuickAddTaskPopover } from "@/components/tasks/QuickAddTask";

const OPTIONS = [
  { key: "task",      label: "Create task",      icon: CheckSquare,  testid: "header-create-task" },
  { key: "project",   label: "Create project",   icon: FolderKanban, to: "/app/projects?new=1", testid: "header-create-project" },
  { key: "milestone", label: "Create milestone", icon: Flag,         to: "/app/projects",       testid: "header-create-milestone",
    toastMsg: "Open a project → Milestones tab to add one" },
  { key: "package",   label: "Create package",   icon: PackageIcon,  to: "/app/packages?new=1", testid: "header-create-package" },
];

export default function HeaderCreateButton() {
  const [open, setOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const { activeWorkspaceId } = useAuth();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Feb 2026 R293zze · Lazy-load the workspace projects list once the
  // Create menu is opened so the Task popover's project dropdown is
  // already warm by the time the user picks "Create task". Falls
  // through silently on error — the dropdown just shows "No project".
  // Feb 2026 R293zzg · Also load workspace members in parallel so the
  // Assignee dropdown is populated (was empty since `members` prop
  // defaulted to []).
  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        if (!projects.length) {
          const r = await api.get(`/workspaces/${activeWorkspaceId}/projects`);
          const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          if (!cancelled) setProjects(items);
        }
      } catch { /* silent */ }
      try {
        if (!members.length) {
          const r = await api.get(`/workspaces/${activeWorkspaceId}/members`);
          const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          if (!cancelled) setMembers(items);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, activeWorkspaceId, projects.length, members.length]);

  const pick = (opt) => {
    setOpen(false);
    if (opt.key === "task") {
      // Open the task popover right here — no navigation to the
      // Tasks module in the background.
      setTaskOpen(true);
      return;
    }
    if (opt.toastMsg) toast.message(opt.toastMsg);
    if (opt.to) navigate(opt.to);
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 border border-border bg-primary text-primary-foreground hover:opacity-90 transition"
        title="Create task, project, milestone or package"
        data-testid="header-create-btn"
      >
        <Plus className="w-3.5 h-3.5" />
        <span className="hidden lg:inline">Create</span>
        <ChevronDown className="w-3 h-3 opacity-80" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-56 bg-background border border-border shadow-xl z-[95]"
          data-testid="header-create-menu"
        >
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => pick(o)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted text-left border-b border-border/50 last:border-b-0"
                data-testid={o.testid}
              >
                <Icon className="w-3.5 h-3.5 text-primary" />
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Feb 2026 R293zze · In-place Task create. No trigger prop →
          the popover falls back to its centered Dialog, so the user
          stays on their current page while filling out the form.
          Three post-save choices are wired below. */}
      {activeWorkspaceId && (
        <QuickAddTaskPopover
          wsId={activeWorkspaceId}
          projects={projects}
          members={members}
          defaultOpen={taskOpen}
          onOpenChange={setTaskOpen}
          showActionChoices
          onSaveAndClose={() => setTaskOpen(false)}
          onSaveAndGotoTasks={() => { setTaskOpen(false); navigate("/app/tasks"); }}
          onSaveAndOpenTask={(task) => {
            setTaskOpen(false);
            if (task?.id) navigate(`/app/tasks?task=${task.id}`);
            else navigate("/app/tasks");
          }}
          testid="header-task-quickadd"
        />
      )}
    </div>
  );
}
